/**
 * SidebarResizeHandle -- Thin vertical drag handle on the right edge of a sidebar.
 *
 * Desktop only (hidden on mobile/tablet).
 * Visual: thin line that becomes more visible on hover; cursor changes to col-resize.
 * Spread `handleProps` from useSidebarResize onto this component.
 *
 * Shared by ChatList and CodeProjectSidebar.
 */

import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SidebarResizeHandleProps {
  /** Props from useSidebarResize().handleProps */
  onMouseDown: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  /** Whether the user is currently dragging */
  isResizing: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SidebarResizeHandle({
  onMouseDown,
  onDoubleClick,
  isResizing,
  className,
}: SidebarResizeHandleProps) {
  return (
    <div
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={undefined}
      tabIndex={-1}
      className={cn(
        // Layout: absolute positioned on right edge of sidebar
        'absolute top-0 right-0 w-[5px] h-full z-[5]',
        // Visual: thin visible line in center
        'before:content-[""] before:absolute before:top-0 before:left-1/2',
        'before:-translate-x-1/2 before:w-px before:h-full',
        'before:bg-border-subtle before:transition-all before:duration-150',
        // Hover: wider and more visible
        'hover:before:w-[3px] hover:before:bg-accent-500/50',
        // Active/dragging: accent color highlight
        isResizing && 'before:w-[3px] before:bg-accent-500',
        // Cursor
        'cursor-col-resize',
        // Selection prevention during drag
        'select-none',
        className,
      )}
    />
  );
}
